import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, ArrowRight, User, Loader2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CTASection from '../components/CTASection';
import SEO, { articleJsonLd } from '../components/SEO';
import { fetchBlog, fetchBlogs } from '../lib/api';

export default function ArticlePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    Promise.all([fetchBlog(id).catch(() => null), fetchBlogs().catch(() => [])])
      .then(([p, all]) => {
        if (!p) {
          setError(true);
          setTimeout(() => navigate('/insights', { replace: true }), 100);
        } else {
          setPost(p);
          setAllPosts(all || []);
        }
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="min-h-screen bg-white flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
        </div>
      </div>
    );
  }

  if (error || !post) return null;

  const idx = allPosts.findIndex((p) => p.slug === post.slug);
  const total = allPosts.length || 1;
  const next = allPosts[(idx + 1) % total] || post;
  const prev = allPosts[(idx - 1 + total) % total] || post;

  return (
    <div>
      <SEO
        title={post.title}
        description={post.excerpt}
        image={post.image}
        path={`/insights/${post.slug}`}
        type="article"
        keywords={post.keywords}
        jsonLd={articleJsonLd(post)}
      />
      <Navbar />

      {/* Header */}
      <section className="relative bg-[#0b0b0d] text-white pt-36 pb-16 overflow-hidden noise">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="absolute right-0 top-0 h-96 w-96 rounded-full bg-amber-500/20 blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-amber-400 transition-colors mb-8"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Insights
          </Link>
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber-400 mb-5">
            {post.category}
          </div>
          <h1 className="font-display text-4xl lg:text-5xl xl:text-6xl font-semibold leading-[1.05]">
            {post.title}
          </h1>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-white/60">
            <span className="inline-flex items-center gap-2">
              <User className="h-4 w-4 text-amber-400" />
              <span className="text-white/90">{post.author}</span>
              <span className="text-white/40">• {post.author_role || post.authorRole}</span>
            </span>
            <span className="inline-flex items-center gap-1.5 font-mono uppercase tracking-wider text-xs">
              <Calendar className="h-3.5 w-3.5" /> {post.date}
            </span>
            <span className="inline-flex items-center gap-1.5 font-mono uppercase tracking-wider text-xs">
              <Clock className="h-3.5 w-3.5" /> {post.read_time}
            </span>
          </div>
        </div>
      </section>

      {/* Hero image */}
      <section className="bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 -mt-12">
          <div className="aspect-[21/9] rounded-2xl overflow-hidden shadow-2xl">
            <img src={post.image} alt={post.title} className="h-full w-full object-cover" />
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="bg-white pt-16 pb-24">
        <article className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="space-y-6">
            {(post.content || []).map((block, i) => {
              if (block.type === 'h2') {
                return (
                  <h2
                    key={i}
                    className="font-display text-2xl lg:text-3xl font-semibold text-[#0b0b0d] mt-10 mb-2 leading-tight"
                  >
                    {block.text}
                  </h2>
                );
              }
              if (block.type === 'p') {
                return (
                  <p key={i} className="text-neutral-700 text-lg leading-relaxed">
                    {block.text}
                  </p>
                );
              }
              if (block.type === 'list') {
                return (
                  <ul key={i} className="space-y-2.5 my-4">
                    {(block.items || []).map((it, j) => (
                      <li
                        key={j}
                        className="flex items-start gap-3 text-neutral-700 text-[17px] leading-relaxed"
                      >
                        <span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                );
              }
              if (block.type === 'quote') {
                return (
                  <blockquote
                    key={i}
                    className="border-l-4 border-amber-500 pl-6 py-2 my-8 font-display text-2xl text-[#0b0b0d] leading-snug italic"
                  >
                    “{block.text}”
                  </blockquote>
                );
              }
              return null;
            })}
          </div>

          <div className="mt-14 pt-10 border-t border-neutral-200 flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
              <User className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <div className="font-semibold text-[#0b0b0d]">{post.author}</div>
              <div className="text-sm text-neutral-500">
                {post.author_role || post.authorRole}
              </div>
            </div>
          </div>
        </article>
      </section>

      {/* Prev / Next */}
      {total > 1 && (
        <section className="bg-[#f4f1eb] py-16 border-t border-neutral-200/60">
          <div className="max-w-5xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-4">
            <Link
              to={`/insights/${prev.slug}`}
              className="card-lift group rounded-2xl bg-white border border-neutral-200 p-6"
            >
              <div className="text-xs uppercase font-mono tracking-wider text-neutral-500 flex items-center gap-2">
                <ArrowLeft className="h-3.5 w-3.5" /> Previous
              </div>
              <div className="mt-3 font-display text-lg font-semibold text-[#0b0b0d] group-hover:text-amber-700 transition-colors">
                {prev.title}
              </div>
            </Link>
            <Link
              to={`/insights/${next.slug}`}
              className="card-lift group rounded-2xl bg-white border border-neutral-200 p-6 md:text-right"
            >
              <div className="text-xs uppercase font-mono tracking-wider text-neutral-500 flex items-center gap-2 md:justify-end">
                Next <ArrowRight className="h-3.5 w-3.5" />
              </div>
              <div className="mt-3 font-display text-lg font-semibold text-[#0b0b0d] group-hover:text-amber-700 transition-colors">
                {next.title}
              </div>
            </Link>
          </div>
        </section>
      )}

      <CTASection />
      <Footer />
    </div>
  );
}
